import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CarouselItem {
  id: string;
  label: string;
  fullLabel?: string;
  icon: React.ReactNode;
  href: string;
  isActive?: boolean;
}

interface MobileCarouselSelectorProps {
  items: CarouselItem[];
  onSelect?: (id: string) => void;
  startIndex?: number;
  variant?: 'marketplace' | 'action';
}

interface SlideNodes {
  scaleNode: HTMLDivElement | null;
  fadeNode: HTMLDivElement | null;
  shortNode: HTMLSpanElement | null;
  fullNode: HTMLSpanElement | null;
}

const MobileCarouselSelector: React.FC<MobileCarouselSelectorProps> = ({
  items,
  onSelect,
  startIndex = 0,
  variant = 'marketplace',
}) => {
  const isMarketplace = variant === 'marketplace';
  const len = items.length;

  // For small item counts (<5), triplicate the items so Embla's loop has
  // enough buffer slides on each side to avoid jitter / sticking with only
  // 3 cards (the wizard "I'm offering" / "I'm looking for" carousels).
  const repeats = len > 0 && len < 5 ? 3 : 1;
  const renderItems = useMemo(() => {
    if (repeats === 1) return items.map((item, i) => ({ item, key: `${item.id}-${i}`, originalIndex: i }));
    const out: { item: CarouselItem; key: string; originalIndex: number }[] = [];
    for (let r = 0; r < repeats; r++) {
      items.forEach((item, i) => {
        out.push({ item, key: `${item.id}-r${r}-${i}`, originalIndex: i });
      });
    }
    return out;
  }, [items, repeats]);

  // Start in the middle copy so swipes work both directions immediately.
  const middleOffset = repeats > 1 ? len * Math.floor(repeats / 2) : 0;
  const effectiveStart = middleOffset + (startIndex % Math.max(len, 1));

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    startIndex: effectiveStart,
    align: 'center',
    containScroll: false,
    dragFree: false,
    skipSnaps: false,
    duration: 22,
  });

  const tweenNodes = useRef<SlideNodes[]>([]);
  const rafId = useRef<number | null>(null);
  const lastCenter = useRef<number>(effectiveStart);
  const [, forceRender] = useState(0);

  const setTweenNodes = useCallback((api: ReturnType<typeof useEmblaCarousel>[1]) => {
    if (!api) return;
    tweenNodes.current = api.slideNodes().map((slideNode) => ({
      scaleNode: slideNode.querySelector('.carousel-slide-inner') as HTMLDivElement | null,
      fadeNode: slideNode.querySelector('.carousel-slide-content') as HTMLDivElement | null,
      shortNode: slideNode.querySelector('.carousel-label-short') as HTMLSpanElement | null,
      fullNode: slideNode.querySelector('.carousel-label-full') as HTMLSpanElement | null,
    }));
  }, []);

  const applyToIndex = useCallback((centerSnap: number) => {
    const total = renderItems.length;
    if (total === 0) return;
    for (let slideIndex = 0; slideIndex < total; slideIndex++) {
      let offset = slideIndex - centerSnap;
      if (offset > total / 2) offset -= total;
      if (offset < -total / 2) offset += total;

      const absOffset = Math.abs(offset);
      const isCenter = absOffset === 0;
      const scale = isCenter ? 1.0 : 0.88;
      const contentOpacity = isCenter ? 1.0 : 0.72;

      const nodes = tweenNodes.current[slideIndex];
      if (!nodes) continue;
      if (nodes.scaleNode) nodes.scaleNode.style.transform = `scale(${scale})`;
      if (nodes.fadeNode) nodes.fadeNode.style.opacity = `${contentOpacity}`;
      const card = nodes.scaleNode?.firstElementChild as HTMLElement | null;
      if (card) {
        if (isCenter) card.setAttribute('data-center', 'true');
        else card.removeAttribute('data-center');
      }
      if (nodes.shortNode && nodes.fullNode) {
        nodes.shortNode.style.display = isCenter ? 'none' : '';
        nodes.fullNode.style.display = isCenter ? '' : 'none';
      }
    }
  }, [renderItems.length]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onScroll = useCallback(() => {
    if (!emblaApi) return;
    if (rafId.current !== null) cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() => {
      const total = renderItems.length;
      if (total === 0) return;
      const scrollProgress = emblaApi.scrollProgress();
      const scrollSnaps = emblaApi.scrollSnapList();

      let nearest = 0;
      let minDiff = Infinity;
      scrollSnaps.forEach((snap, idx) => {
        let diff = Math.abs(snap - scrollProgress);
        if (diff > 0.5) diff = 1 - diff;
        if (diff < minDiff) {
          minDiff = diff;
          nearest = idx;
        }
      });

      if (nearest !== lastCenter.current) {
        lastCenter.current = nearest;
        applyToIndex(nearest);
      }
    });
  }, [emblaApi, renderItems.length, applyToIndex]);

  const onSelect_ = useCallback(() => {
    if (!emblaApi) return;
    const newSelected = emblaApi.selectedScrollSnap();
    lastCenter.current = newSelected;
    applyToIndex(newSelected);
    if (onSelect) {
      const original = renderItems[newSelected]?.originalIndex ?? 0;
      const item = items[original];
      if (item) onSelect(item.id);
    }
    forceRender((n) => n + 1);
  }, [emblaApi, onSelect, items, renderItems, applyToIndex]);

  useEffect(() => {
    if (!emblaApi) return;
    const init = () => {
      setTweenNodes(emblaApi);
      lastCenter.current = emblaApi.selectedScrollSnap();
      applyToIndex(lastCenter.current);
    };
    init();
    emblaApi.on('scroll', onScroll);
    emblaApi.on('reInit', init);
    emblaApi.on('select', onSelect_);
    return () => {
      emblaApi.off('scroll', onScroll);
      emblaApi.off('reInit', init);
      emblaApi.off('select', onSelect_);
      if (rafId.current !== null) cancelAnimationFrame(rafId.current);
    };
  }, [emblaApi, setTweenNodes, applyToIndex, onScroll, onSelect_]);

  return (
    <div className="relative px-2">
      <button
        onClick={scrollPrev}
        className={cn(
          "absolute left-0 top-1/2 -translate-y-1/2 z-10",
          "w-8 h-8 rounded-full",
          "bg-black/30 backdrop-blur-sm",
          "flex items-center justify-center",
          "text-white/80 hover:text-white hover:bg-black/50",
          "transition-colors duration-200",
          "active:scale-95"
        )}
        aria-label="Previous"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      <div ref={emblaRef} className="overflow-hidden mx-4">
        <div className="flex -ml-1">
          {renderItems.map(({ item, key }) => (
            <div
              key={key}
              className="flex-shrink-0 min-w-0 pl-1 basis-1/3"
            >
              <Link
                to={item.href}
                className="carousel-slide-inner block w-full will-change-transform"
              >
                <div
                  className={cn(
                    "bg-white/35 dark:bg-black/30 backdrop-blur-md",
                    "rounded-2xl overflow-hidden",
                    isMarketplace ? "h-24 py-2" : "h-32 py-3",
                    "data-[center=true]:ring-inset data-[center=true]:ring-2 data-[center=true]:ring-white/50",
                    item.isActive && "data-[center=true]:bg-white/50 dark:data-[center=true]:bg-black/40 data-[center=true]:ring-white/70"
                  )}
                >
                  <div className="carousel-slide-content flex flex-col items-center justify-center h-full will-change-[opacity]">
                    <div className={cn(
                      "flex items-center justify-center",
                      isMarketplace ? "w-12 h-12 mb-1" : "w-16 h-16 mb-2"
                    )}>
                      {item.icon}
                    </div>
                    <span className={cn(
                      "text-white font-medium text-center px-1 leading-tight",
                      isMarketplace ? "text-[9px]" : "text-[10px]"
                    )}>
                      <span className="carousel-label-short">{item.label}</span>
                      {item.fullLabel && (
                        <span className="carousel-label-full" style={{ display: 'none' }}>
                          {item.fullLabel}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>

      <button
        onClick={scrollNext}
        className={cn(
          "absolute right-0 top-1/2 -translate-y-1/2 z-10",
          "w-8 h-8 rounded-full",
          "bg-black/30 backdrop-blur-sm",
          "flex items-center justify-center",
          "text-white/80 hover:text-white hover:bg-black/50",
          "transition-colors duration-200",
          "active:scale-95"
        )}
        aria-label="Next"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </div>
  );
};

export default MobileCarouselSelector;
